import { Card } from "./Card";
import update from "immutability-helper";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { IoMdTrash } from "react-icons/io";
import {
  DragDropContext,
  DropResult,
  Droppable,
  Draggable,
} from "react-beautiful-dnd";
import { PolicyBuilderContentItem } from "../../../types/api";

interface PolicyContentInputContainerProps {
  value: PolicyBuilderContentItem[];
  onChanged: (value: PolicyBuilderContentItem[]) => void;
  index?: string | number;
  canDelete?: boolean;
  deleteItem?: () => void;
}

const grid = 8;

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  ...draggableStyle,
});

const getListStyle = (isDraggingOver: boolean) => ({
  padding: grid,
});

const PolicyContentInputContainer: React.FC<
  PolicyContentInputContainerProps
> = ({ value, onChanged, index = "top", canDelete = true, deleteItem }) => {
  const updateCardData = (index: number, card: PolicyBuilderContentItem) => {
    const newCollection = update(value, { [index]: { $set: card } });
    onChanged(newCollection);
  };

  const renderCard = (
    card: {
      content: string;
      items: PolicyBuilderContentItem[];
      title: string;
    },
    currInd: number,
    deleteCard: () => void
  ) => {
    return (
      <Card
        key={index}
        index={index}
        id={index}
        card={card}
        deleteCard={deleteCard}
        updateCard={(v) => updateCardData(currInd, v)}
      />
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) return;

    const dragCard = value[source.index];
    onChanged(
      update(value, {
        $splice: [
          [source.index, 1],
          [destination.index, 0, dragCard],
        ],
      })
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={`droppable-${index}`} type="droppableItem">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
          >
            {value.map((item, iIndex) => (
              <Draggable
                key={`drag-${index}-${iIndex}`}
                draggableId={`drag-${index}-${iIndex}`}
                index={iIndex}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(
                      snapshot.isDragging,
                      provided.draggableProps.style
                    )}
                  >
                    {renderCard(item, iIndex, () => {
                      const newItems = value.filter((ev, id) => id !== iIndex);
                      onChanged(newItems);
                    })}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}

            <div className="flex">
              <button
                type="button"
                onClick={() => {
                  onChanged([...value, { content: "", items: [], title: "" }]);
                }}
                className="bg-green-500 text-white flex items-center justify-center p-2 rounded-md text-xs mr-2 cursor-pointer hover:bg-green-800 hover:text-white"
              >
                <BsFillPlusCircleFill className="mr-2" /> Add Subsection
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={deleteItem}
                  className="bg-red-500 text-white flex items-center justify-center p-2 rounded-md text-xs mr-2 cursor-pointer hover:bg-red-800 hover:text-white"
                >
                  <IoMdTrash className="mr-2" size={14} /> Delete
                </button>
              )}
            </div>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default PolicyContentInputContainer;
